/**
 * AccountSection - Account authentication component
 *
 * Pure presentational component showing sign-in/sign-out options.
 * All authentication logic and handlers in parent.
 */
import { View } from "react-native";
import { useTranslation } from "@dramadetector/shared";
import { TierBody } from "@dramadetector/ui";
import { GlassCard } from "../../../components/glass";
import { SettingsItem } from "../../../components/SettingsItem";
import type { AuthUser } from "../../../contexts/AuthContext";

interface AccountSectionProps {
  /** Current authenticated user (null if not signed in) */
  user: AuthUser | null;
  /** Sign out callback (supports both sync and async) */
  onSignOut: () => void | Promise<void>;
}

export function AccountSection({ user, onSignOut }: AccountSectionProps) {
  const { t } = useTranslation('settings');

  return (
    <View className="mt-4" testID="account-section">
      <TierBody size="xs" weight="semibold" className="text-gray-500 uppercase px-4 mb-2">
        {t('sections.account')}
      </TierBody>
      <GlassCard>
        {user ? (
          <>
            <SettingsItem
              icon="👤"
              label={t('account_actions.signed_in_as')}
              value={user.email || "No email"}
              testID="user-email"
            />
            <View className="border-t border-gray-200" />
            <SettingsItem
              icon="🚪"
              label={t('account_actions.sign_out')}
              onPress={onSignOut}
              destructive
              testID="sign-out-button"
            />
          </>
        ) : (
          <SettingsItem
            icon="👤"
            label={t('account_actions.sign_in')}
            description={t('about.coming_soon')}
          />
        )}
      </GlassCard>
    </View>
  );
}
